<template>
  <div
    v-if="enabled"
    class="google-login"
    :class="`google-login--${variant}`"
    :style="{ '--google-button-width': `${buttonOptions().width || 40}px` }"
  >
    <button
      v-if="!agreed"
      class="google-login-start"
      type="button"
      :disabled="disabled"
      :aria-label="variant === 'icon' ? devSyncText(label) : null"
      :title="devSyncText(label)"
      @click="$emit('agreement-required')"
    >
      <svg
        class="google-login-mark"
        viewBox="0 0 18 18"
        aria-hidden="true"
        focusable="false"
      >
        <path
          fill="#4285F4"
          d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.875 2.684-6.614Z"
        />
        <path
          fill="#34A853"
          d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.86-3.047.86-2.344 0-4.328-1.584-5.036-3.712H.957v2.332A9 9 0 0 0 9 18Z"
        />
        <path
          fill="#FBBC05"
          d="M3.964 10.709A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.709V4.959H.957A9 9 0 0 0 0 9c0 1.452.347 2.827.957 4.041l3.007-2.332Z"
        />
        <path
          fill="#EA4335"
          d="M9 3.58c1.321 0 2.507.454 3.44 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .957 4.959l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z"
        />
      </svg>
      <span v-if="variant !== 'icon'">{{ devSyncText(displayLabel) }}</span>
    </button>
    <template v-else>
      <div
        v-show="ready && !binding && !busy && !disabled"
        ref="button"
        class="google-login-official"
      ></div>
      <!-- 正在验证 Google 账号… -->
      <!-- 正在连接 Google… -->
      <p
        v-if="(loading || busy) && variant === 'standard'"
        class="google-login-status"
        role="status"
      >
        {{
          busy
            ? $t("devUpdates.oct23GoogleVerifying")
            : $t("devUpdates.oct23GoogleConnecting")
        }}
      </p>
      <!-- 正在验证 Google 账号 -->
      <!-- 正在连接 Google -->
      <span
        v-if="(loading || busy) && variant !== 'standard'"
        class="google-login-unavailable"
        :data-tooltip="
          busy
            ? $t('devUpdates.oct23GoogleVerifyingShort')
            : $t('devUpdates.oct23GoogleConnectingShort')
        "
        tabindex="0"
      >
        <!-- 正在验证 Google 账号 -->
        <!-- 正在连接 Google -->
        <button
          type="button"
          class="google-login-start google-login-start--unavailable"
          disabled
          :aria-label="
            busy
              ? $t('devUpdates.oct23GoogleVerifyingShort')
              : $t('devUpdates.oct23GoogleConnectingShort')
          "
        >
          <svg
            class="google-login-mark"
            viewBox="0 0 18 18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="#4285F4"
              d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.875 2.684-6.614Z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.86-3.047.86-2.344 0-4.328-1.584-5.036-3.712H.957v2.332A9 9 0 0 0 9 18Z"
            />
            <path
              fill="#FBBC05"
              d="M3.964 10.709A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.709V4.959H.957A9 9 0 0 0 0 9c0 1.452.347 2.827.957 4.041l3.007-2.332Z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.321 0 2.507.454 3.44 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .957 4.959l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z"
            />
          </svg>
          <span v-if="variant !== 'icon'">{{ devSyncText(displayLabel) }}</span>
        </button>
      </span>
      <div
        v-if="error && variant === 'standard'"
        class="google-login-error"
        role="alert"
      >
        {{ devSyncText(error) }}
      </div>
      <span
        v-if="error && variant !== 'standard' && !binding"
        class="google-login-unavailable"
        :data-tooltip="error"
        tabindex="0"
      >
        <!-- {label}不可用：{error} -->
        <button
          type="button"
          class="google-login-start google-login-start--unavailable"
          disabled
          :aria-label="
            $t('googleFeedback.unavailableLabel', {
              label: displayLabel,
              error: error,
            })
          "
        >
          <svg
            class="google-login-mark"
            viewBox="0 0 18 18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              fill="#4285F4"
              d="M17.64 9.205c0-.638-.057-1.252-.164-1.841H9v3.482h4.844a4.14 4.14 0 0 1-1.797 2.715v2.258h2.909c1.702-1.567 2.684-3.875 2.684-6.614Z"
            />
            <path
              fill="#34A853"
              d="M9 18c2.43 0 4.468-.806 5.956-2.181l-2.909-2.258c-.806.54-1.835.86-3.047.86-2.344 0-4.328-1.584-5.036-3.712H.957v2.332A9 9 0 0 0 9 18Z"
            />
            <path
              fill="#FBBC05"
              d="M3.964 10.709A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.709V4.959H.957A9 9 0 0 0 0 9c0 1.452.347 2.827.957 4.041l3.007-2.332Z"
            />
            <path
              fill="#EA4335"
              d="M9 3.58c1.321 0 2.507.454 3.44 1.346l2.581-2.581C13.464.892 11.426 0 9 0A9 9 0 0 0 .957 4.959l3.007 2.332C4.672 5.164 6.656 3.58 9 3.58Z"
            />
          </svg>
          <span v-if="variant !== 'icon'">{{ devSyncText(displayLabel) }}</span>
        </button>
      </span>
      <!-- 重新连接 Google -->
      <button
        v-else-if="!ready && !loading && !binding"
        type="button"
        class="google-login-start"
        :disabled="disabled || busy"
        @click="prepare"
      >
        {{ $t("devUpdates.oct23ReconnectGoogle") }}
      </button>
      <!-- 验证已有平台账号 -->
      <form
        v-if="binding"
        class="google-login-binding"
        :aria-label="$t('devUpdates.oct23VerifyExistingAccount')"
        @submit.prevent="submitBinding"
      >
        <!-- 该邮箱已有平台账号，请输入原平台登录密码以绑定 Google。 -->
        <p>{{ $t("devUpdates.oct23GoogleBindingHint") }}</p>
        <label>
          <!-- 平台登录密码 -->
          <span>{{ $t("devUpdates.oct23PlatformPassword") }}</span>
          <input
            v-model="password"
            type="password"
            autocomplete="current-password"
            maxlength="20"
            :disabled="busy"
            required
          />
        </label>
        <div class="google-login-binding-actions">
          <!-- 验证并登录 -->
          <button type="submit" :disabled="busy || disabled || !password">
            {{ $t("devUpdates.oct23VerifyAndSignIn") }}
          </button>
          <!-- 取消 -->
          <button type="button" :disabled="busy" @click="cancelBinding">
            {{ $t("business.cancel") }}
          </button>
        </div>
      </form>
    </template>
  </div>
</template>

<script>
import bindingRequest from "@/utils/request";
const bindGoogleIdentity = (data) => bindingRequest.post("/api/mts-api/user/loginBindings", { kind: "google", credential: data.credential });
import devSyncI18n from "@/i18n/common/index";

import {
  getGoogleLoginConfig,
  getGoogleLoginChallenge,
  googleLogin,
} from "@/api/login";
import { loadGoogleIdentity } from "@/utils/googleIdentity";
import { encryptPassword } from "@/utils/passwordEncrypt";

export default {
  mixins: [devSyncI18n],
  name: "GoogleLoginButton",
  props: {
    bindingMode: { type: Boolean, default: false },
    agreed: { type: Boolean, default: false },
    disabled: { type: Boolean, default: false },
    referralParams: { type: Function, default: () => ({}) },
    label: { type: String, default: "Google 登录" },
    variant: {
      type: String,
      default: "standard",
      validator: (value) => ["standard", "action", "icon"].includes(value),
    },
  },
  data() {
    return {
      enabled: true,
      loading: false,
      ready: false,
      busy: false,
      binding: false,
      password: "",
      error: "",
    };
  },
  computed: {
    displayLabel() {
      // 注册
      // Google 注册
      // 登录
      if (this.variant === "action")
        // 注册
        return this.label.includes(this.$t("common.register"))
          ? // Google 注册
            this.$t("devUpdates.oct23GoogleSignUp")
          : // 登录
            this.$t("common.login");
      // 使用 Google 账号继续
      return this.$t("devUpdates.googleContinue");
    },
  },
  watch: {
    agreed() {
      this.reset();
      if (this.agreed) this.prepare();
    },
  },
  mounted() {
    this._generation = 0;
    this._alive = true;
    this._googleCredential = "";
    this.prepare();
  },
  beforeDestroy() {
    this._alive = false;
    this.reset();
  },
  methods: {
    // 与未勾选占位按钮共用尺寸；仅通过官方参数调整，不覆盖或遮挡 Google iframe。
    buttonOptions() {
      const registration = (this.label || "").includes("注册");
      const locale =
        { "zh-CN": "zh_CN", "zh-HK": "zh_TW", "en-US": "en" }[
          this.$store?.state?.locale?.uiLocale
        ] || "zh_CN";
      return this.variant === "icon"
        ? {
            type: "icon",
            theme: "outline",
            size: "large",
            shape: "square",
            locale,
          }
        : {
            type: "standard",
            theme: "outline",
            size: "large",
            shape: "rectangular",
            locale,
            text:
              this.variant === "action"
                ? registration
                  ? "signup_with"
                  : "signin"
                : "continue_with",
            width: this.variant === "action" ? (registration ? 180 : 108) : 280,
            logo_alignment: "left",
          };
    },
    reset() {
      this._generation = (this._generation || 0) + 1;
      this._googleCredential = "";
      this.binding = false;
      this.password = "";
      this.ready = false;
      this.loading = false;
      this.error = "";
      if (this.busy) this.$emit("busy", false);
      this.busy = false;
      if (this.$refs.button) this.$refs.button.innerHTML = "";
    },
    async prepare() {
      if (this.loading || this.busy || !this._alive) return;
      const generation = ++this._generation;
      const current = () => this._alive && generation === this._generation;
      this.loading = true;
      this.ready = false;
      this.error = "";
      try {
        const config = await getGoogleLoginConfig();
        if (!current()) return;
        // Google 登录配置暂不可用
        if (config.code !== 200)
          throw new Error(
            // Google 登录配置暂不可用
            config.msg || this.$t("googleFeedback.configurationUnavailable")
          );
        this.enabled = Boolean(config.data?.enabled && config.data?.clientId);
        if (!this.enabled || !this.agreed) return;
        const sdk = await loadGoogleIdentity();
        if (!current() || !this.agreed) return;
        const challenge = await getGoogleLoginChallenge();
        if (!current() || !this.agreed) return;
        // Google 登录初始化失败
        if (challenge.code !== 200 || !challenge.data?.nonce)
          throw new Error(
            // Google 登录初始化失败
            challenge.msg || this.$t("googleFeedback.initializationFailed")
          );
        sdk.initialize({
          client_id: config.data.clientId,
          nonce: challenge.data.nonce,
          auto_select: false,
          ux_mode: "popup",
          callback: (response) => {
            if (
              !current() ||
              !this.agreed ||
              !this.ready ||
              this.disabled ||
              this.busy ||
              this.binding
            )
              return;
            if (!response?.credential) return;
            this._googleCredential = response.credential;
            this.submitCredential();
          },
        });
        await this.$nextTick();
        if (!current() || !this.$refs.button) return;
        this.$refs.button.innerHTML = "";
        sdk.renderButton(this.$refs.button, this.buttonOptions());
        this.ready = true;
      } catch (error) {
        // Google 登录暂不可用，请重试
        if (current())
          this.error =
            // Google 登录暂不可用，请重试
            error.message || this.$t("googleFeedback.unavailableRetry");
      } finally {
        if (current()) this.loading = false;
      }
    },
    async submitCredential(platformPassword) {
      if (!this.agreed || this.disabled || this.busy || !this._googleCredential)
        return;
      const generation = this._generation;
      const current = () => this._alive && generation === this._generation;
      this.busy = true;
      this.error = "";
      this.$emit("busy", true);
      try {
        const encrypted = platformPassword
          ? await encryptPassword(platformPassword)
          : undefined;
        if (!current()) return;
        const response = await (this.bindingMode ? bindGoogleIdentity : googleLogin)({
          ...this.referralParams(),
          credential: this._googleCredential,
          ...(encrypted ? { password: encrypted } : {}),
        });
        if (!current()) return;
        this.password = "";
        if (response.code === 200) {
          this._googleCredential = "";
          this.binding = false;
          this.ready = false;
          // 先解除表单锁，随后交由宿主沿用既有的登录跳转。
          this.busy = false;
          this.$emit("busy", false);
          this.$emit("success", response);
        } else if (response.code === 409) {
          this.binding = true;
          // 请验证已有平台账号
          this.error = response.msg || this.$t("googleFeedback.verifyAccount");
        } else {
          this._googleCredential = "";
          this.binding = false;
          this.ready = false;
          // Google 登录失败，请重新连接
          this.error = response.msg || this.$t("googleFeedback.reconnect");
        }
      } catch (error) {
        if (current()) {
          this.password = "";
          // 网络错误后重新授权；不保留可能已被后端消费过的凭证。
          this._googleCredential = "";
          this.binding = false;
          this.ready = false;
          // Google 登录失败，请重新连接
          this.error = error.message || this.$t("googleFeedback.reconnect");
        }
      } finally {
        if (current() && this.busy) {
          this.busy = false;
          this.$emit("busy", false);
        }
      }
    },
    submitBinding() {
      if (this.password) this.submitCredential(this.password);
    },
    cancelBinding() {
      if (this.busy) return;
      this.reset();
      this.prepare();
    },
  },
};
</script>

<style scoped lang="less">
.google-login {
  width: 100%;
  margin: 20px auto 0;
  text-align: center;
  .google-login-official {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 40px;
    min-height: 40px;
  }
  .google-login-start {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
    box-sizing: border-box;
    width: var(--google-button-width, 280px);
    max-width: 100%;
    height: 40px;
    min-height: 40px;
    padding: 0 12px;
    border: 1px solid #747775;
    border-radius: 4px;
    background: #fff;
    color: #1f1f1f;
    font-family: Roboto, Arial, sans-serif;
    font-size: 14px;
    font-weight: 500;
    white-space: nowrap;
    cursor: pointer;
    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &.google-login-start--unavailable {
      color: #98a2b3;
      border-color: #dfe3e8;
      background: #f3f4f6;
      box-shadow: none;
      cursor: not-allowed;
      opacity: 1;

      .google-login-mark {
        color: #98a2b3;
      }
    }
  }
  .google-login-mark {
    display: block;
    width: 18px;
    height: 18px;
    flex: 0 0 18px;
    stroke: none;
  }
  .google-login-unavailable {
    position: relative;
    display: inline-flex;

    &::before,
    &::after {
      position: absolute;
      left: 50%;
      z-index: 20;
      opacity: 0;
      pointer-events: none;
      transition: opacity 0.15s ease, transform 0.15s ease;
    }

    &::before {
      bottom: calc(100% + 4px);
      border: 6px solid transparent;
      border-top-color: #344054;
      content: "";
      transform: translate(-50%, 4px);
    }

    &::after {
      bottom: calc(100% + 16px);
      width: max-content;
      max-width: 280px;
      padding: 7px 10px;
      border-radius: 6px;
      color: #fff;
      background: #344054;
      box-shadow: 0 4px 12px rgba(16, 24, 40, 0.18);
      content: attr(data-tooltip);
      font-size: 12px;
      line-height: 1.5;
      text-align: left;
      white-space: normal;
      transform: translate(-50%, 4px);
    }

    &:hover,
    &:focus-visible {
      outline: none;

      &::before,
      &::after {
        opacity: 1;
        transform: translate(-50%, 0);
      }
    }
  }
  .google-login-status {
    color: #6b7280;
    font-size: 13px;
    margin: 10px 0;
  }
  .google-login-error {
    color: #c34035;
    font-size: 12px;
    line-height: 1.6;
    margin: 8px 0;
  }
  .google-login-binding {
    max-width: 340px;
    margin: 0 auto;
    text-align: left;
    p {
      font-size: 13px;
      line-height: 1.6;
      color: #526070;
    }
    label {
      display: block;
      span {
        display: block;
        font-size: 12px;
        margin-bottom: 6px;
        color: #526070;
      }
      input {
        box-sizing: border-box;
        width: 100%;
        height: 42px;
        padding: 0 10px;
        border: 1px solid #d7dde5;
        border-radius: 6px;
        font-size: 16px;
      }
    }
    .google-login-binding-actions {
      display: flex;
      gap: 10px;
      margin-top: 12px;
      button {
        flex: 1;
        height: 38px;
        border: 1px solid #d7dde5;
        border-radius: 6px;
        background: white;
        color: #2563eb;
        cursor: pointer;
        &:disabled {
          opacity: 0.6;
          cursor: default;
        }
      }
    }
  }

  &.google-login--action,
  &.google-login--icon {
    display: contents;

    .google-login-official,
    .google-login-start {
      order: 0;
    }

    .google-login-status,
    .google-login-error,
    .google-login-binding {
      flex: 0 0 100%;
      order: 1;
      box-sizing: border-box;
      width: 100%;
    }

    .google-login-status,
    .google-login-error {
      margin: 8px 0 0;
      text-align: left;
    }
  }

  &.google-login--action {
    .google-login-official {
      width: var(--google-button-width);
      flex: 0 0 var(--google-button-width);
      justify-content: flex-start;
    }

    .google-login-start {
      width: var(--google-button-width);
      flex: 0 0 var(--google-button-width);
      min-width: var(--google-button-width);
      height: 40px;
      min-height: 40px;
      justify-content: flex-start;
      gap: 12px;
      padding: 0 12px;
      border-radius: 4px;
      box-shadow: none;

      > span {
        flex: 1;
        text-align: center;
      }
    }
  }

  &.google-login--icon {
    .google-login-official {
      width: 40px;
      flex: 0 0 40px;
      justify-content: center;
    }

    .google-login-start {
      display: inline-flex;
      width: 40px;
      flex: 0 0 40px;
      min-width: 40px;
      height: 40px;
      min-height: 40px;
      align-items: center;
      justify-content: center;
      padding: 0;
      border-radius: 4px;
      box-shadow: none;
    }
  }
}
</style>
